import React, {useEffect} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {Controller, useForm} from 'react-hook-form';
import {FormControlLabel, Radio, RadioGroup} from '@mui/material';
import {
    AddressInput,
    CreateChancel,
    SelectAutocompleteMultiple,
    Input,
    CheckboxCustom,
    DateCalendarForm,
    MaskInputMain,
    renderApptAddressGroup,
} from 'components';
import {FindLoad, FindSuccessItem, FindErrorItem, sortListByKey, useModal} from 'utils';
import {clientActions, fundingSourceActions, httpRequestsOnErrorsActions, httpRequestsOnSuccessActions} from 'store';
import {GET_FS_SOURCES, clientRelationship, clientRelationshipEnums, insuredFillGroups} from './constants';
import {enrollmentTypeEnums} from '../clientSingle/core/constants';

const toInsuredFields = (person, address) => ({
    insuredFirstName: person?.firstName || '',
    insuredMiddleName: person?.middleName || '',
    insuredLastName: person?.lastName || '',
    insuredPhone: person?.phoneNumber || '',
    insuredAddress: address || null,
});

export const AddEnrollment = ({info}) => {
    const {close} = useModal();
    const dispatch = useDispatch();
    const ACTION_TYPE = info ? 'EDIT_CLIENT_ENROLLMENT' : 'CREATE_CLIENT_ENROLLMENT';
    const fSelect = useSelector((state) => state.fundingSource.fSelect.funders);
    const enrolments = useSelector((state) => state.client.clientEnrollment);
    const client = useSelector((state) => state.client.clientItemInfo);
    const clientContacts = useSelector((state) => state.client.clientContacts);
    const loader = FindLoad(ACTION_TYPE);
    const backError = FindErrorItem(ACTION_TYPE);
    const success = FindSuccessItem(ACTION_TYPE);

    const {
        handleSubmit,
        control,
        setError,
        watch,
        reset,
        setValue,
    } = useForm({
        defaultValues: {
            isPrimary: false,
            insuredRelationship: '',
        },
    });
    // "Fill from" list: the client (Self) first, then the contacts — same order and labels as the Contacts tab.
    const clientAddress = client?.addresses?.find((a) => a.default)?.address || client?.addresses?.[0]?.address;
    const fillOptions = [
        ...(client?.id ? [{...client, address: clientAddress, group: insuredFillGroups.CLIENT}] : []),
        ...sortListByKey(clientContacts, 'fullName').map((contact) => ({...contact, group: insuredFillGroups.CLIENT_CONTACT})),
    ];
    const requiredRule = (message) => (value) => !!(typeof value === 'string' ? value.trim() : value) || message;

    const fundingSources = fSelect?.filter(function (array_el) {
        return (
            enrolments?.filter(function (anotherOne_el) {
                return anotherOne_el.payerId === array_el?.id && !anotherOne_el?.terminationDate;
            }).length === 0
        );
    });

    useEffect(() => {
        if (client?.id) dispatch(clientActions.getClientsContacts(client.id));
    }, [client?.id]);

    const setInsured = (values) => Object.entries(values).forEach(([field, value]) => setValue(field, value));

    const handleFillFrom = (option) => {
        if (!option) return;
        setInsured(toInsuredFields(option, option.address));
        if (option.group === insuredFillGroups.CLIENT) setValue('insuredRelationship', clientRelationshipEnums.SELF);
    };

    useEffect(() => {
        if (info) {
            const params = {
                funding: info?.payerId,
                enrollmentDate: info?.enrollmentDate,
                isPrimary: info?.type === enrollmentTypeEnums.PRIMARY,
                enrollmentId: info?.enrollmentId,
                insuredRelationship: info?.insuredInfo?.relationshipType || '',
                ...toInsuredFields(info?.insuredInfo, info?.insuredInfo?.address),
            };
            info?.expirationDate ? (params.expirationDate = info?.expirationDate) : delete params.expirationDate;
            reset(params);
        }
    }, [info]);

    useEffect(() => {
        if (success) {
            dispatch(httpRequestsOnSuccessActions.removeSuccess(ACTION_TYPE));
            close();
        }
    }, [success]);

    useEffect(() => {
        if (
            backError?.error === 'Enrolment ID must be unique' ||
            backError?.error === 'Cannot re-add the same payer with the same member ID'
        ) {
            dispatch(httpRequestsOnErrorsActions.removeError(ACTION_TYPE));
            setError('enrollmentId', {message: backError?.error});
        }
    }, [backError]);

    useEffect(() => {
        return () => {
            dispatch(httpRequestsOnErrorsActions.removeError(ACTION_TYPE));
        };
    }, []);

    const handleCreate = (data) => {
        const params = {
            enrollmentId: data.enrollmentId,
            enrollmentDate: data.enrollmentDate,
            expirationDate: data?.expirationDate ? data.expirationDate : null,
            type: data.isPrimary ? enrollmentTypeEnums.PRIMARY : enrollmentTypeEnums.SECONDARY,
            insuredInfo: {
                relationshipType: data.insuredRelationship,
                firstName: data.insuredFirstName?.trim(),
                middleName: data.insuredMiddleName?.trim() || null,
                lastName: data.insuredLastName?.trim(),
                phoneNumber: data.insuredPhone || null,
                address: data.insuredAddress,
            }
        };

        if (info) {
            dispatch(clientActions.editClientEnrollment(params, info.id));
        } else {
            dispatch(clientActions.createClientEnrollment(params, client.id, data?.funding));
        }
    };

    return (
        <div style={{width: 480}}>
            <p className="modal-header-title">{info ? 'Edit Payer' : 'Add Payer'}</p>
            <form onSubmit={handleSubmit(handleCreate)}>
                <div className="modal-body-wrapper">
                    <div className='flex-align-start' style={{gap: 16}}>
                        <SelectAutocompleteMultiple
                            loadType={GET_FS_SOURCES}
                            name={'funding'}
                            label={'Payer*'}
                            control={control}
                            options={fundingSources || []}
                            rules={{required: 'Payer is required.'}}
                            renderValue={(option) => option?.name}
                            disabled={!!info}
                            defaultSelected={info?.payerId && {name: info?.payerName}}
                            refresh={() => {
                                dispatch(fundingSourceActions.getFundingSource({status: 'ACTIVE'}));
                            }}
                        />
                        <Input
                            name="enrollmentId"
                            label="Member ID*"
                            variant="outlined"
                            control={control}
                            maxLength={20}
                            rules={{
                                required: 'Member ID is required.',
                                validate: (value) => value.trim().length > 0 || 'Member ID cannot be empty spaces.',
                            }}
                        />
                    </div>

                    <div className='flex-align-start' style={{gap: 16}}>
                        <DateCalendarForm
                            name={'enrollmentDate'}
                            label={'Enrollment Date*'}
                            control={control}
                            rules={{required: 'Enrollment Date is required.'}}
                            max={watch('expirationDate') || undefined}
                        />
                        <DateCalendarForm
                            name={'expirationDate'}
                            label={'Expiration Date'}
                            control={control}
                            min={watch('enrollmentDate') || undefined}
                        />
                    </div>

                    <div>
                        <CheckboxCustom
                            padding={'0 9px 0 0'}
                            color={watch('isPrimary') ? '#0C66E4' : '#D0D5DD'}
                            name={'isPrimary'}
                            label={'Set as Primary'}
                            control={control}
                        />
                    </div>

                    <div className="enrollment-insured-header">
                        <p className="enrollment-insured-title">Insured Information</p>
                    </div>
                    <SelectAutocompleteMultiple
                        name={'fillFrom'}
                        label={'Fill from client or contact'}
                        placeholder={'Select the client or a contact to autofill'}
                        control={control}
                        options={fillOptions}
                        groupBy={(option) => option.group}
                        renderGroup={renderApptAddressGroup}
                        renderValue={(option) => `${option?.firstName} ${option?.lastName ? option?.lastName : ''}`}
                        handleChange={handleFillFrom}
                    />
                    <div className="flex-align-start" style={{gap: 16}}>
                        <Input
                            name="insuredFirstName"
                            label="Insured Name*"
                            variant="outlined"
                            control={control}
                            rules={{validate: requiredRule('Insured Name is required.')}}
                        />
                        <Input
                            name="insuredMiddleName"
                            label="Insured Middle Name"
                            variant="outlined"
                            control={control}
                        />
                    </div>
                    <div className="flex-align-start" style={{gap: 16}}>
                        <Input
                            name="insuredLastName"
                            label="Insured Last Name*"
                            variant="outlined"
                            control={control}
                            rules={{validate: requiredRule('Insured Last Name is required.')}}
                        />
                        <MaskInputMain
                            name="insuredPhone"
                            label="Insured Phone Number"
                            variant="outlined"
                            control={control}
                        />
                    </div>
                    <Controller
                        name="insuredAddress"
                        control={control}
                        rules={{validate: requiredRule('Street Address is required.')}}
                        render={({field: {onChange, value}, fieldState: {error}}) => (
                            <AddressInput
                                placeholder={'Street Address*'}
                                selectedAddress={value}
                                setCurrentAddress={(val) => onChange(val?.street ? val : null)}
                                errMessage={error?.message}
                            />
                        )}
                    />
                    <div className="client-relationship-box">
                        <p>Relationship to client:</p>
                        <Controller
                            name="insuredRelationship"
                            control={control}
                            rules={{validate: requiredRule('Relationship is required.')}}
                            render={({field: {onChange, value}, fieldState: {error}}) => (
                                <RadioGroup
                                    name="insuredRelationship"
                                    value={value}
                                    onChange={onChange}
                                >
                                    {clientRelationship.map((item) => (
                                        <FormControlLabel
                                            key={item.value}
                                            value={item.value}
                                            label={item.title}
                                            control={<Radio style={{
                                                color: value === item.value ? '#0C66E4' : error ? '#F04438' : '#D0D5DD',
                                                padding: 0
                                            }}/>}
                                        />
                                    ))}
                                </RadioGroup>
                            )}
                        />
                    </div>
                </div>
                <CreateChancel
                    type="submit"
                    loader={!!loader.length}
                    create={info ? 'Save Changes' : 'Add'}
                    chancel={'Cancel'}
                    onClose={close}
                    buttonWidth="100%"
                    cancelRight={'24px'}
                />
            </form>
        </div>
    );
};
